import { useState } from 'react'
import styled from 'styled-components'

//Importing Dynamic Form Components
import Page from '../components/pageView/Page'
import CreateOrgAccounting from './CreateOrgAccounting'

//Getting Hooks
import { useNavigate } from 'react-router-dom'
import useApi from '../hooks/useApi'
import useAuth from '../hooks/useAuth'
import useTranslation from '../hooks/useTranslation'
import useOrg from '../hooks/useOrg'
import useProfile from '../hooks/useProfile'
import pause from '../utils/pause'


const FlexWrapper = styled.section`
  display: flex;
  justify-content: center
`
const Container = styled.div`
  padding: 10px;
  background-color: ${({ theme }) => { theme.card.background }};
`
const Title = styled.h1`
  font-size: var(--font-XL);
  font-weight: bold;
`

const SubTitle = styled.h2`
  font-size: var(--font-M);
  font-weight: lighter;
`

function CreateOrg() {

  const [step, setStep] = useState(1)
  const { getMe } = useApi()
  const { Auth } = useAuth()
  const { switchOrg } = useOrg()
  // `pState` carries the org the profile was just initialized from — including
  // its country, which step 3 uses to suggest a base currency. Reading it here
  // rather than fetching the org again keeps a cosmetic prefill from being one
  // more request that can fail between two steps of a wizard.
  const { initializeProfile, pState } = useProfile()
  const userEmail = Auth.email

  const navigate = useNavigate()

  const { t } = useTranslation()

  /*
   * The org exists from here on, with the country's own chart already cloned.
   * Switching into it before step 3 is what makes that step possible at all:
   * the chart list, the settings write and the period generation are all
   * org-scoped calls, and until the token names this org none of them can run.
   */
  const handleSaveOrg = async () => {
    await pause(1500);
    // Retrieve the updated user
    const userData = await getMe(`user/email/${Auth.email}`)
    const user = userData.data

    /*
     * Switching in is what makes step 3 possible — the chart list, the settings
     * write and the period generation are all org-scoped, and until the token
     * names this org none of them can run. But `switchOrg` ends by refetching
     * every cached query, and a single rejection among them used to throw out
     * of here before the wizard could advance, leaving the user looking at a
     * saved form with nowhere to go. Whether the refetch succeeded is not
     * allowed to decide whether the next step appears.
     */
    try {
      await switchOrg(user.activeOrgId)
      await initializeProfile()
    } catch (err) {
      console.error('[createOrg] could not switch into the new organization', err)
    }

    setStep(3)
  }

  const handleFinish = async () => {
    // The setup step writes the org's currency and its VAT scheme, so the
    // profile in memory is stale by the time it finishes.
    await initializeProfile()
    navigate('/home')
  }

  const handleSaveUser = async () => {
    //await pause(1500);
    setStep(2)
  }


  const dispatch = (action, payload) => {
    if (action === 'back') { setStep(1); return null }
    if (action === 'saveNContinue' & step === 1) { handleSaveUser() }
    if (action === 'saveNContinue' & step === 2) { handleSaveOrg() }

  }

  return (
    <FlexWrapper>
      <Container>
        {/* NAME FORM  */}
        {(step === 1) ? (
          <>
            <Title data-testid='createOrg.user.title'>{t('createOrg.form.user.title')}</Title>
            <SubTitle data-testid='createOrg.user.subtitle'>{t('createOrg.form.user.subtitle')}</SubTitle>
            <br></br>
            <Page type='createOrgCardUser' dispatch={dispatch} options={{ mode: 'edit', load:true,forcedId: userEmail, forcedPath: 'user/email' }} />
          </>

        ) : <></>}
        {/* ORG FORM  */}
        {(step === 2) ? (
          <>
            <Title data-testid='createOrg.org.title'>{t('createOrg.form.org.title')}</Title>
            <SubTitle data-testid='createOrg.org.subtitle'>{t('createOrg.form.org.subtitle')}</SubTitle>
            <br></br>
            <Page type='createOrgCardOrganization' dispatch={dispatch} options={{ mode: 'new' }} />
          </>
        ) : <></>}
        {/* ACCOUNTING SETUP  */}
        {(step === 3) ? (
          <>
            <Title data-testid='createOrg.accounting.title'>{t('createOrg.form.accounting.title')}</Title>
            <SubTitle data-testid='createOrg.accounting.subtitle'>{t('createOrg.form.accounting.subtitle')}</SubTitle>
            <br></br>
            <CreateOrgAccounting country={pState.currentOrgCountry} onDone={handleFinish} />
          </>
        ) : <></>}
      </Container>
    </FlexWrapper>
  )

}

export default CreateOrg